/**
 * CollaborationFeature — Wires existing Yjs infrastructure into MarthaEditor.
 * Uses CollaborativeContext + useDocumentCollaboration + PresenceAvatars.
 */

import { useEffect, useMemo } from 'react';
import { useDocumentCollaboration } from '../../../hooks/useCollaboration';
import { PresenceAvatars } from '../../documentation/PresenceAvatars';
import { Wifi, WifiOff } from 'lucide-react';

interface CollaborationFeatureProps {
  documentId: string;
  userId: string;
  userName: string;
  enabled: boolean;
  children?: React.ReactNode;
}

/**
 * Hook that returns Yjs collaboration config for useCreateBlockNote.
 * Returns null when collaboration is disabled or not connected.
 */
export function useCollaborationConfig({
  documentId,
  userId,
  userName,
  enabled,
}: {
  documentId?: string;
  userId?: string;
  userName?: string;
  enabled: boolean;
}) {
  const collab = useDocumentCollaboration({
    docId: documentId || '',
    userId: userId || 'anonymous',
    userName: userName || 'Anonymous',
    enabled: enabled && !!documentId,
  });

  const config = useMemo(() => {
    if (!enabled || !collab.ydoc || !collab.isConnected) return null;

    return {
      fragment: collab.ydoc.getXmlFragment('document-content'),
      user: {
        name: userName || 'Anonymous',
        color: '#f97316', // coral
      },
      provider: collab.awareness ? { awareness: collab.awareness } : undefined,
    };
  }, [enabled, collab.ydoc, collab.isConnected, collab.awareness, userName]);

  return {
    config,
    isConnected: collab.isConnected,
    isConnecting: collab.isConnecting,
    connectedUsers: collab.connectedUsers,
    connectionError: collab.connectionError,
    updateCursor: collab.updateCursor,
    clearCursor: collab.clearCursor,
  };
}

/**
 * Collaboration header strip — presence avatars + connection status.
 */
export function CollaborationHeader({
  documentId,
  userId,
  userName,
  enabled,
}: CollaborationFeatureProps) {
  const { isConnected, isConnecting, connectedUsers } = useCollaborationConfig({
    documentId,
    userId,
    userName,
    enabled,
  });

  if (!enabled) return null;

  return (
    <div className="flex items-center gap-2 px-4 py-1.5 border-b border-stone-100 bg-stone-50">
      <PresenceAvatars
        users={connectedUsers}
        currentUserId={userId}
        isConnected={isConnected}
        isConnecting={isConnecting}
      />
    </div>
  );
}

/**
 * Offline banner when WebSocket disconnects.
 */
export function OfflineBanner({ isConnected, enabled }: { isConnected: boolean; enabled: boolean }) {
  if (!enabled || isConnected) return null;

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-700 text-sm">
      <WifiOff size={14} />
      <span>You're offline. Changes will sync when reconnected.</span>
    </div>
  );
}
